/**
 * Support Inbox — the thread list (design 2026-09-26, section 6.3).
 *
 * Reads two endpoints of `backend/src/routes/admin-support-inbox.ts`:
 *
 *   GET /v1/admin/support-inbox/summary   support_inbox.read   (tab counts)
 *   GET /v1/admin/support-inbox/threads   support_inbox.read   (the rows)
 *
 * Markup only. Every rule — the query, the offset reset, the 403 path, the
 * tab set — lives in `useSupportInboxPage.ts` and `data.ts`, where root jest
 * can execute it (`tests/unit/admin/support-inbox-page.test.ts`).
 *
 * No message text reaches this page: a row carries the subject, the sender,
 * the member link, the status, the assignee and the verdict chips — the
 * fields the list endpoint returns and nothing more.
 */
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { SortableHeader } from '@/components/table/SortableHeader';
import { Skeleton } from '@/components/ui/Skeleton';
import { resolveUserDisplayName } from '@/lib/utils';
import {
  COPY,
  GROUP_LABELS,
  SENDER_NOT_VERIFIED_CHIP,
  STATUS_FILTER_OPTIONS,
  STATUS_LABELS,
  STATUS_PILL_CLASSES,
  VERDICT_CHIP_CLASSES,
  formatWhen,
  groupCount,
  senderLabel,
  unansweredBadge,
  unansweredRestrictedBadge,
  verdictChips,
  type SupportThreadRow,
  type ThreadStatus,
} from './data';
import { useSupportInboxPage } from './useSupportInboxPage';

const COLUMNS = 7;

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <tr key={i} className="border-b">
          <td className="px-4 py-3">
            <Skeleton className="h-5 w-16 rounded-full" />
          </td>
          <td className="px-4 py-3">
            <div className="space-y-2">
              <Skeleton className="h-4 w-56" />
              <Skeleton className="h-3 w-24" />
            </div>
          </td>
          <td className="px-4 py-3">
            <Skeleton className="h-4 w-40" />
          </td>
          <td className="px-4 py-3">
            <Skeleton className="h-4 w-24" />
          </td>
          <td className="px-4 py-3">
            <Skeleton className="h-5 w-16 rounded-full" />
          </td>
          <td className="px-4 py-3">
            <Skeleton className="h-4 w-24" />
          </td>
          <td className="px-4 py-3">
            <Skeleton className="h-4 w-32" />
          </td>
        </tr>
      ))}
    </>
  );
}

function StatusPill({ status }: { status: ThreadStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_PILL_CLASSES[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

function ThreadRow({ row }: { row: SupportThreadRow }) {
  const chips = verdictChips(row.verdicts);
  return (
    <tr className="border-b transition-colors hover:bg-muted/50">
      <td className="px-4 py-3 align-top">
        <span
          className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-400"
          title={GROUP_LABELS[row.group]}
        >
          {row.mailbox}
        </span>
      </td>
      <td className="px-4 py-3 align-top">
        <Link to={`/support-inbox/${row.id}`} className="font-medium hover:underline">
          {row.subject?.trim() || '(no subject)'}
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          <span>
            {row.message_count} {row.message_count === 1 ? 'message' : 'messages'}
          </span>
          {!row.sender_verified && !row.is_machine && (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-400">
              {SENDER_NOT_VERIFIED_CHIP}
            </span>
          )}
          {row.virus_flagged && (
            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950 dark:text-red-400">
              virus flagged
            </span>
          )}
          {chips.map((c) => (
            <span
              key={c.key}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${VERDICT_CHIP_CLASSES[c.tone]}`}
            >
              {c.label}
            </span>
          ))}
        </div>
      </td>
      <td className="px-4 py-3 align-top text-sm">
        <span className="break-all">{senderLabel(row.first_from_address, row.first_from_name)}</span>
      </td>
      <td className="px-4 py-3 align-top text-sm">
        {row.member ? (
          <Link to={`/users/${row.member.id}`} className="text-primary hover:underline">
            {resolveUserDisplayName(row.member)}
          </Link>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        <StatusPill status={row.status} />
      </td>
      <td className="px-4 py-3 align-top text-sm">
        {row.assigned_admin_display_name ?? <span className="text-muted-foreground">Unassigned</span>}
      </td>
      <td className="px-4 py-3 align-top text-sm text-muted-foreground">{formatWhen(row.last_message_at)}</td>
    </tr>
  );
}

export function SupportInboxPage() {
  const c = useSupportInboxPage();

  const totalPages = Math.ceil(c.total / c.pageSize);
  const currentPage = Math.floor(c.offset / c.pageSize) + 1;
  const hasPrev = c.offset > 0;
  const hasNext = c.offset + c.pageSize < c.total;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-2xl font-bold">{COPY.title}</h2>
            {unansweredBadge(c.summary) !== null && (
              <span
                className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-400"
                role="status"
                aria-label="Threads waiting more than 24 hours"
              >
                {unansweredBadge(c.summary)}
              </span>
            )}
            {unansweredRestrictedBadge(c.summary) !== null && (
              <span
                className="inline-flex items-center rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-950 dark:text-rose-400"
                role="status"
                aria-label="Restricted threads waiting more than 24 hours"
              >
                {unansweredRestrictedBadge(c.summary)}
              </span>
            )}
          </div>
          <p className="text-muted-foreground">{COPY.subtitle}</p>
        </div>
        <button
          type="button"
          onClick={c.refresh}
          disabled={c.loading}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
          aria-label="Refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {c.accessDenied ? (
        <div className="rounded-lg border bg-card p-8 text-center">
          <h3 className="mb-2 text-lg font-semibold">Access denied</h3>
          <p className="text-muted-foreground">{COPY.accessDenied}</p>
        </div>
      ) : (
        <>
          {/* Group tabs, with the summary's counts */}
          <div className="flex flex-wrap gap-1" role="tablist" aria-label="Mailbox group">
            {c.tabs.map((tab) => {
              const active = c.group === tab.key;
              const n = groupCount(c.summary, tab.key);
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => c.setGroup(tab.key)}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-colors ${
                    active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {tab.label}
                  {n !== null && (
                    <span
                      className={`rounded-full px-1.5 text-xs ${
                        active ? 'bg-primary-foreground/20' : 'bg-muted'
                      }`}
                    >
                      {n}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground" htmlFor="support-inbox-status">
                Status:
              </label>
              <select
                id="support-inbox-status"
                value={c.status}
                onChange={(e) => c.setStatus(e.target.value as ThreadStatus | '')}
                aria-label="Filter by status"
                className="h-10 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={c.assigned}
                onChange={(e) => c.setAssigned(e.target.checked)}
                aria-label={COPY.assignedToMe}
                className="h-4 w-4 rounded border"
              />
              {COPY.assignedToMe}
              {c.summary && c.summary.assigned_to_me > 0 && (
                <span className="rounded-full bg-muted px-1.5 text-xs">{c.summary.assigned_to_me}</span>
              )}
            </label>

            <input
              type="search"
              value={c.q}
              onChange={(e) => c.setQ(e.target.value)}
              placeholder={COPY.searchPlaceholder}
              aria-label="Search threads"
              maxLength={200}
              className="h-10 flex-1 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary sm:max-w-sm"
            />
          </div>

          {c.error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {c.error}
            </div>
          )}

          {/* The table */}
          <div className="rounded-lg border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Mailbox</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Subject</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">From</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Member</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Assignee</th>
                    <SortableHeader
                      field="last_message_at"
                      label="Last activity"
                      activeField={c.sort.field}
                      activeOrder={c.sort.order}
                      onSort={c.setSortField}
                    />
                  </tr>
                </thead>
                <tbody>
                  {c.loading && c.rows.length === 0 ? (
                    <SkeletonRows />
                  ) : c.rows.length === 0 ? (
                    <tr>
                      <td colSpan={COLUMNS} className="px-4 py-12 text-center text-sm text-muted-foreground">
                        {COPY.empty}
                      </td>
                    </tr>
                  ) : (
                    c.rows.map((row) => <ThreadRow key={row.id} row={row} />)
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination — the feedback page's shape */}
            {totalPages > 0 && (
              <div className="flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {c.total === 0 ? 0 : c.offset + 1} to {Math.min(c.offset + c.pageSize, c.total)} of{' '}
                  {c.total} threads
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => c.setOffset(Math.max(0, c.offset - c.pageSize))}
                    disabled={!hasPrev}
                    className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                    title="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Prev
                  </button>
                  <span className="px-2 text-sm">
                    Page {currentPage} of {totalPages || 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => c.setOffset(c.offset + c.pageSize)}
                    disabled={!hasNext}
                    className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                    title="Next page"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default SupportInboxPage;
